<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import CallDevicesAPI from 'dashboard/api/callDevices';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();
const up = patch => emit('update', patch);

const callDevices = ref([]);
onMounted(async () => {
  try {
    const { data } = await CallDevicesAPI.index();
    callDevices.value = Array.isArray(data) ? data : [];
  } catch (e) {
    callDevices.value = [];
  }
});

// Modos com ícone + descrição (cards, não pílulas).
const MODES = [
  { value: 'outbound', icon: 'i-lucide-phone-outgoing' },
  { value: 'inbound', icon: 'i-lucide-phone-incoming' },
  { value: 'both', icon: 'i-lucide-phone-call' },
];
const WHO_FIRST = ['caller', 'ai'];
const SENSITIVITY = ['low', 'medium', 'high'];
const RESPONSE_SPEEDS = ['fast', 'normal', 'patient'];
// Vozes da Realtime (system voice). Padrão marin.
const VOICES = [
  'marin',
  'cedar',
  'ash',
  'ballad',
  'coral',
  'sage',
  'verse',
  'alloy',
  'echo',
  'shimmer',
];

const mode = computed(() => props.data.voice_mode || 'outbound');
const whoFirst = computed(() => props.data.voice_who_first || 'caller');
const sensitivity = computed(() => props.data.voice_sensitivity || 'medium');
const speed = computed(() => props.data.voice_response_speed || 'normal');
const voice = computed(() => props.data.voice_realtime_voice || 'marin');
const halfDuplex = computed(() => props.data.voice_half_duplex !== false);
// Default ON (barge-in + AEC); false só quando desligado explicitamente.
const interruptOn = computed(() => props.data.voice_allow_interrupt !== false);
const maxMinutes = computed(() => props.data.voice_max_minutes || 5);

const INTERRUPT_SENSITIVITIES = ['low', 'medium', 'high'];
const VOICEMAIL_ACTIONS = ['hangup', 'message'];

const interruptSens = computed(
  () => props.data.voice_interrupt_sensitivity || 'medium'
);
const silencePrompt = computed(
  () => props.data.voice_silence_prompt_secs || 10
);
const silenceHangup = computed(
  () => props.data.voice_silence_hangup_secs || 20
);
const speechSpeed = computed(() => props.data.voice_speech_speed || 1.0);
const voicemail = computed(() => props.data.voice_voicemail_action || 'hangup');
const showOutbound = computed(() => mode.value !== 'inbound');
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Cabeçalho -->
    <div
      class="flex items-center justify-between border-b border-n-weak px-4 py-3"
    >
      <div class="flex items-center gap-2">
        <span
          class="grid size-7 place-items-center rounded-lg bg-n-brand/15 text-n-brand"
        >
          <span class="i-lucide-phone text-base" />
        </span>
        <div>
          <h3 class="text-sm font-semibold leading-tight text-n-slate-12">
            {{ t('AI_AGENTS.FLOW.VOICE.TITLE') }}
          </h3>
          <p class="text-[11px] leading-tight text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.VOICE.DESCRIPTION') }}
          </p>
        </div>
      </div>
      <button
        class="i-lucide-x text-n-slate-10 hover:text-n-slate-12"
        @click="emit('close')"
      />
    </div>

    <div class="flex-1 space-y-3 overflow-y-auto p-4">
      <!-- ░░ Telefone & modo ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-smartphone text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.VOICE.SEC_PHONE') }}
          </span>
        </div>

        <!-- Telefone (device) -->
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.DEVICE') }}
        </label>
        <select
          v-if="callDevices.length"
          :value="data.call_device_id || ''"
          class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          @change="up({ call_device_id: $event.target.value })"
        >
          <option value="">{{ t('AI_AGENTS.FLOW.VOICE.DEVICE_NONE') }}</option>
          <option v-for="d in callDevices" :key="d.id" :value="d.id">
            {{ d.name }}
          </option>
        </select>
        <p v-else class="mb-3 text-[11px] text-n-amber-11">
          {{ t('AI_AGENTS.FLOW.VOICE.NO_DEVICE') }}
        </p>

        <!-- Modo: cards -->
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.MODE') }}
        </label>
        <div class="mb-3 space-y-1.5">
          <button
            v-for="m in MODES"
            :key="m.value"
            type="button"
            class="flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition-all"
            :class="
              mode === m.value
                ? 'border-n-brand bg-n-brand/10'
                : 'border-n-weak hover:border-n-slate-8'
            "
            @click="up({ voice_mode: m.value })"
          >
            <span
              class="text-base"
              :class="[
                m.icon,
                mode === m.value ? 'text-n-brand' : 'text-n-slate-10',
              ]"
            />
            <span class="flex-1">
              <span class="block text-xs font-medium text-n-slate-12">
                {{ t(`AI_AGENTS.FLOW.VOICE.MODES.${m.value.toUpperCase()}`) }}
              </span>
              <span class="block text-[10px] text-n-slate-10">
                {{
                  t(`AI_AGENTS.FLOW.VOICE.MODE_DESC.${m.value.toUpperCase()}`)
                }}
              </span>
            </span>
            <span
              v-if="mode === m.value"
              class="i-lucide-check text-sm text-n-brand"
            />
          </button>
        </div>

        <!-- Voz -->
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.VOICE_LABEL') }}
        </label>
        <div class="flex flex-wrap gap-1.5">
          <button
            v-for="v in VOICES"
            :key="v"
            type="button"
            class="rounded-full border px-2.5 py-1 text-[11px] capitalize transition-all"
            :class="
              voice === v
                ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                : 'border-n-weak text-n-slate-11 hover:border-n-slate-8'
            "
            @click="up({ voice_realtime_voice: v })"
          >
            {{ v }}
          </button>
        </div>
      </section>

      <!-- ░░ Conversa ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-message-square text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.VOICE.SEC_CONVERSATION') }}
          </span>
        </div>

        <!-- Quem fala primeiro: segmented -->
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.WHO_FIRST_LABEL') }}
        </label>
        <div
          class="mb-3 flex rounded-lg border border-n-weak bg-n-solid-1 p-0.5"
        >
          <button
            v-for="w in WHO_FIRST"
            :key="w"
            type="button"
            class="flex-1 rounded-md py-1.5 text-[11px] font-medium transition-all"
            :class="
              whoFirst === w
                ? 'bg-n-brand text-white shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="up({ voice_who_first: w })"
          >
            {{ t(`AI_AGENTS.FLOW.VOICE.WHO_FIRST.${w.toUpperCase()}`) }}
          </button>
        </div>

        <!-- Saudação (só quando a IA fala primeiro) -->
        <template v-if="whoFirst === 'ai'">
          <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.VOICE.GREETING') }}
          </label>
          <textarea
            :value="data.voice_greeting_message || ''"
            rows="2"
            maxlength="300"
            :placeholder="t('AI_AGENTS.FLOW.VOICE.GREETING_PH')"
            class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
            @input="up({ voice_greeting_message: $event.target.value })"
          />
          <p class="mb-3 text-[10px] text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.VOICE.GREETING_HINT') }}
          </p>
        </template>

        <!-- Despedida -->
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.FAREWELL') }}
        </label>
        <textarea
          :value="data.voice_farewell_message || ''"
          rows="2"
          maxlength="300"
          :placeholder="t('AI_AGENTS.FLOW.VOICE.FAREWELL_PH')"
          class="mb-1 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-2 text-sm text-n-slate-12 focus:border-n-brand focus:outline-none"
          @input="up({ voice_farewell_message: $event.target.value })"
        />
        <p class="text-[10px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.VOICE.FAREWELL_HINT') }}
        </p>
      </section>

      <!-- ░░ Interrupção ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-hand text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.VOICE.SEC_INTERRUPT') }}
          </span>
        </div>

        <!-- Sensibilidade auditiva geral: segmented -->
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.SENSITIVITY') }}
        </label>
        <div class="flex rounded-lg border border-n-weak bg-n-solid-1 p-0.5">
          <button
            v-for="s in SENSITIVITY"
            :key="s"
            type="button"
            class="flex-1 rounded-md py-1.5 text-[11px] font-medium transition-all"
            :class="
              sensitivity === s
                ? 'bg-n-brand text-white shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="up({ voice_sensitivity: s })"
          >
            {{ t(`AI_AGENTS.FLOW.VOICE.SENS.${s.toUpperCase()}`) }}
          </button>
        </div>
        <p class="mb-3 mt-1 text-[10px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.VOICE.SENSITIVITY_HINT') }}
        </p>

        <!-- Permitir interrupção -->
        <div class="flex items-center justify-between gap-3 py-1.5">
          <div class="min-w-0">
            <p class="text-xs font-medium text-n-slate-12">
              {{ t('AI_AGENTS.FLOW.VOICE.ALLOW_INTERRUPT') }}
            </p>
            <p class="text-[10px] leading-tight text-n-slate-10">
              {{ t('AI_AGENTS.FLOW.VOICE.ALLOW_INTERRUPT_HINT') }}
            </p>
          </div>
          <button
            class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
            :class="interruptOn ? 'bg-n-brand' : 'bg-n-slate-5'"
            @click="up({ voice_allow_interrupt: !interruptOn })"
          >
            <span
              class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
              :class="interruptOn ? 'left-[18px]' : 'left-0.5'"
            />
          </button>
        </div>

        <!-- Sensibilidade de interrupção: condicional -->
        <div v-if="interruptOn" class="mt-2">
          <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.VOICE.INTERRUPT_SENS') }}
          </label>
          <div class="flex rounded-lg border border-n-weak bg-n-solid-1 p-0.5">
            <button
              v-for="s in INTERRUPT_SENSITIVITIES"
              :key="s"
              type="button"
              class="flex-1 rounded-md py-1.5 text-[11px] font-medium transition-all"
              :class="
                interruptSens === s
                  ? 'bg-n-brand text-white shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="up({ voice_interrupt_sensitivity: s })"
            >
              {{ t(`AI_AGENTS.FLOW.VOICE.INT_SENS.${s.toUpperCase()}`) }}
            </button>
          </div>
          <p class="mt-1 text-[10px] text-n-slate-10">
            {{ t('AI_AGENTS.FLOW.VOICE.INTERRUPT_SENS_HINT') }}
          </p>
        </div>
      </section>

      <!-- ░░ Tempos ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-clock text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.VOICE.SEC_TIMING') }}
          </span>
        </div>

        <!-- Tempo pra responder: segmented -->
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.SPEED') }}
        </label>
        <div class="flex rounded-lg border border-n-weak bg-n-solid-1 p-0.5">
          <button
            v-for="sp in RESPONSE_SPEEDS"
            :key="sp"
            type="button"
            class="flex-1 rounded-md py-1.5 text-[11px] font-medium transition-all"
            :class="
              speed === sp
                ? 'bg-n-brand text-white shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="up({ voice_response_speed: sp })"
          >
            {{ t(`AI_AGENTS.FLOW.VOICE.SPEEDS.${sp.toUpperCase()}`) }}
          </button>
        </div>
        <p class="mb-3 mt-1 text-[10px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.VOICE.SPEED_HINT') }}
        </p>

        <!-- Silêncio antes de perguntar "você está aí?": slider -->
        <div class="mb-1 flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.VOICE.SILENCE_PROMPT') }}
          </label>
          <span class="text-xs font-semibold tabular-nums text-n-brand">
            {{ t('AI_AGENTS.FLOW.VOICE.SECONDS', { n: silencePrompt }) }}
          </span>
        </div>
        <input
          type="range"
          min="5"
          max="60"
          step="1"
          :value="silencePrompt"
          class="mb-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-n-slate-5 accent-n-brand"
          @input="
            up({ voice_silence_prompt_secs: Number($event.target.value) })
          "
        />

        <!-- Silêncio antes de encerrar: slider -->
        <div class="mb-1 flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.VOICE.SILENCE_HANGUP') }}
          </label>
          <span class="text-xs font-semibold tabular-nums text-n-brand">
            {{ t('AI_AGENTS.FLOW.VOICE.SECONDS', { n: silenceHangup }) }}
          </span>
        </div>
        <input
          type="range"
          min="10"
          max="120"
          step="5"
          :value="silenceHangup"
          class="mb-3 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-n-slate-5 accent-n-brand"
          @input="
            up({ voice_silence_hangup_secs: Number($event.target.value) })
          "
        />

        <!-- Duração máxima: slider -->
        <div class="mb-1 flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.VOICE.MAX_MINUTES') }}
          </label>
          <span class="text-xs font-semibold tabular-nums text-n-brand">
            {{ t('AI_AGENTS.FLOW.VOICE.MINUTES', { n: maxMinutes }) }}
          </span>
        </div>
        <input
          type="range"
          min="1"
          max="30"
          step="1"
          :value="maxMinutes"
          class="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-n-slate-5 accent-n-brand"
          @input="up({ voice_max_minutes: Number($event.target.value) })"
        />
      </section>

      <!-- ░░ Voz ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-audio-lines text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.VOICE.SEC_VOICE') }}
          </span>
        </div>

        <!-- Velocidade da fala: slider -->
        <div class="mb-1 flex items-center justify-between">
          <label class="text-xs font-medium text-n-slate-11">
            {{ t('AI_AGENTS.FLOW.VOICE.SPEECH_SPEED') }}
          </label>
          <span class="text-xs font-semibold tabular-nums text-n-brand">
            {{
              t('AI_AGENTS.FLOW.VOICE.SPEECH_SPEED_VALUE', {
                n: speechSpeed.toFixed(2),
              })
            }}
          </span>
        </div>
        <input
          type="range"
          min="0.8"
          max="1.2"
          step="0.05"
          :value="speechSpeed"
          class="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-n-slate-5 accent-n-brand"
          @input="up({ voice_speech_speed: Number($event.target.value) })"
        />
      </section>

      <!-- ░░ Outbound (só quando o modo permite ligar) ░░ -->
      <section v-if="showOutbound" class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span class="i-lucide-voicemail text-[13px] text-n-slate-10" />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.VOICE.SEC_OUTBOUND') }}
          </span>
        </div>
        <label class="mb-1.5 block text-xs font-medium text-n-slate-11">
          {{ t('AI_AGENTS.FLOW.VOICE.VOICEMAIL') }}
        </label>
        <div class="flex rounded-lg border border-n-weak bg-n-solid-1 p-0.5">
          <button
            v-for="a in VOICEMAIL_ACTIONS"
            :key="a"
            type="button"
            class="flex-1 rounded-md py-1.5 text-[11px] font-medium transition-all"
            :class="
              voicemail === a
                ? 'bg-n-brand text-white shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="up({ voice_voicemail_action: a })"
          >
            {{ t(`AI_AGENTS.FLOW.VOICE.VOICEMAIL_OPTS.${a.toUpperCase()}`) }}
          </button>
        </div>
        <p class="mt-1 text-[10px] text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.VOICE.VOICEMAIL_HINT') }}
        </p>
      </section>

      <!-- ░░ Avançado ░░ -->
      <section class="rounded-xl border border-n-weak p-3">
        <div class="mb-3 flex items-center gap-1.5">
          <span
            class="i-lucide-sliders-horizontal text-[13px] text-n-slate-10"
          />
          <span
            class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('AI_AGENTS.FLOW.VOICE.SEC_ADVANCED') }}
          </span>
        </div>

        <!-- Anti-eco (legado) -->
        <div class="flex items-center justify-between gap-3 py-1.5">
          <div class="min-w-0">
            <p class="text-xs font-medium text-n-slate-12">
              {{ t('AI_AGENTS.FLOW.VOICE.HALF_DUPLEX') }}
            </p>
            <p class="text-[10px] leading-tight text-n-slate-10">
              {{ t('AI_AGENTS.FLOW.VOICE.HALF_DUPLEX_LEGACY_HINT') }}
            </p>
          </div>
          <button
            class="relative h-5 w-9 shrink-0 rounded-full transition-colors"
            :class="halfDuplex ? 'bg-n-brand' : 'bg-n-slate-5'"
            @click="up({ voice_half_duplex: !halfDuplex })"
          >
            <span
              class="absolute top-0.5 size-4 rounded-full bg-white transition-all"
              :class="halfDuplex ? 'left-[18px]' : 'left-0.5'"
            />
          </button>
        </div>
      </section>
    </div>

    <!-- Remover -->
    <div class="border-t border-n-weak p-3">
      <button
        class="flex w-full items-center justify-center gap-1.5 rounded-lg border border-n-ruby-7 py-2 text-xs font-medium text-n-ruby-9 transition-colors hover:bg-n-ruby-3"
        @click="emit('remove')"
      >
        <span class="i-lucide-trash-2 text-sm" />
        {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
      </button>
    </div>
  </div>
</template>
